import { Store } from '@ngxs/store';
import { Component, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { take, filter } from 'rxjs/operators';
import { ServerApi } from '../services/api.services/server.api';
import { AppState } from '../Store/app.state/app.state';
import { ISite } from '../Store/sites.state/sites.model';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionApiSvc } from '../services/api.services/session.api.svc';
import { ApiTools } from '../services/api.services/api.tools';
import {
  GoToProxyViewpoint,
  SetEmptyActiveSite,
  SetSelectedMode
} from '../Store/app.state/app.actions';
import { SseApiSvc } from '../services/api.services/SSE/sse.api.svc';
import { PermissionsManager } from '../services/permissions-manager';
import { CMD_ACTIONS, CMD_TARGETS, CmdRouterService } from '../services/cmd-router.service';
import { isNullOrUndefined } from '../helper';
import { SetAllSitesNoDefault } from '../Store/sites.state/sites.actions';
import { APPLICATION_MODE, IAppConf, SCENE_MODE } from '../Store/app.state/app.model';
import { environment } from '../../environments/environment';
import { SitesLoaderSvc } from '../services/api.services/sites.loader.svc';
import { CoordinatesType, IPlacemark } from '../Store/placemarks.state/placemarks.model';
import { PlacemarksApiSvc } from '../services/api.services/placemarks.api.svc';
import { IntositeEmbeddedService } from '../services/external/intosite-embedded.service';
import { SitesApiSvc } from '../services/api.services/sites.api.svc';
import { MessageAndType, PriorityStack, StatusService } from '../services/status.service';
import { ViewerObjectType } from '../common/Models/UI/viewer-object-type.enum';
import { PointCloudSvc } from '../services/api.services/point-cloud.service';
import { ContentContainer } from '../Viewer/content.container/content.container';
import { NgStyle } from '@angular/common';
import { SetLayer } from '../Store/layers.state/layers.actions';
import { SetZone } from '../Store/zones.state/zones.actions';
import { LAYER_VISIBILITY } from '../Store/layers.state/layers.model';
import { ZonesState } from '../Store/zones.state/zones.state';
import { LayersState } from '../Store/layers.state/layers.state';

@Component({
    selector: 'ins-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    imports: [ContentContainer, NgStyle]
})
export class MainComponent implements OnInit {

  vpProxyParams: string;
  proxyChangedFromStart: boolean = true;
  activeSite$: Observable<ISite>;
  logoutToggleIndex$: Observable<number>;

  appConf$: Observable<IAppConf>;
  public appConf: IAppConf;
  public APPLICATION_MODES: any = APPLICATION_MODE;

  selectedMode$: Observable<SCENE_MODE>;
  public sceneMode: SCENE_MODE;

  constructor(private readonly store: Store, private readonly serverApi: ServerApi, private readonly route: ActivatedRoute, private readonly placemarksApiSvc: PlacemarksApiSvc, private readonly sessionApiSvc: SessionApiSvc,
    private readonly sseApiSvc: SseApiSvc, private readonly router: Router, private readonly cmdRouterSvc: CmdRouterService, private readonly siteApiSvc: SitesApiSvc, private readonly statusService: StatusService,
    private readonly siteLoaderSvc: SitesLoaderSvc, private readonly pointCloudSvc: PointCloudSvc, private readonly intositeEmbeddedService: IntositeEmbeddedService) {
    this.activeSite$ = this.store.select(AppState.getActiveSite);
    this.logoutToggleIndex$ = this.store.select(AppState.getLogoutToggleIndex);
    this.appConf$ = this.store.select(AppState.getAppConf);
    this.selectedMode$ = this.store.select(AppState.getSelectedMode);
    window.addEventListener('storage', this.storageChange.bind(this));
    if (!environment.production) {
      window.addEventListener('keydown', this.keyDownDebug.bind(this));
    }
    const sessionId: string = localStorage.getItem('intosite_session');
    PermissionsManager.initManager(sessionId);
    PermissionsManager.initPermBeforeGetPermFromServer();
    this.selectedMode$.subscribe((mode: SCENE_MODE) => {
      this.sceneMode = mode;
    });

    this.appConf$.subscribe( (appConf: IAppConf) => {
      this.appConf = appConf;
    });

    this.route.queryParams.subscribe((params: any) => {
      if (this.router.url === '/' || this.router.url.startsWith('/?intosite-proxy=yes') || (params['intosite-site-viewpoint'] || params['intosite-object-viewpoint'])) {
        // for debug only!
        store.subscribe((state: any) => {
          (window as any).store = state;
        });

        this.runExampleFlow();
      }
    });
  }

  storageChange(event$: any): void {
    if (event$.key === 'proxyPath') {
      this.proxyChangedFromStart = false;
      const params = JSON.parse('{"' + event$.newValue.replace(/&/g, '","').replace(/=/g,'":"') + '"}', (key, value) => key===""?value:decodeURIComponent(value));
      this.goToProxyViewpoint(params);
      this.serverApi.isSiteAlreadyLoaded.next(true);
      window.focus(); 
      localStorage.removeItem('proxyPath');
    }
  }

  keyDownDebug(event$: any): void {
    if (event$.ctrlKey && event$.altKey && event$.key === 'u') {
      const sampleString: string = '';
      this.proxyChangedFromStart = false;
      this.goToProxyViewpoint(sampleString);
      this.serverApi.isSiteAlreadyLoaded.next(true);
    }
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params: any) => {
      if (params['intosite-site-viewpoint'] || params['intosite-object-viewpoint']) {
        this.proxyChangedFromStart = true;
        this.goToProxyViewpoint(params);
        console.log('changed', this.vpProxyParams);
      }
    });
  }

  public goToProxyViewpoint(params: any): void {
    this.vpProxyParams = params;
    this.setViewpointFromProxy(params);
    console.log('changed', this.vpProxyParams);
  }

  public async runExampleFlow(): Promise<any> {
    await this.sessionApiSvc.getLibraryIcons();
    // await this.sessionApiSvc.getCurrentUser();
    // await this.sseApiSvc.regSse();

    this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.MAP_MANAGER, CMD_ACTIONS.CLEAR_SCENE);
    this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.WEBGL_MANAGER, CMD_ACTIONS.CLEAR_SCENE); // need to check - map / facility / pano
    this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.PANORAMIC_MANAGER, CMD_ACTIONS.CLEAR_PANORAMIC_VIEWER);
    this.pointCloudSvc.setPointCloudProvider();

    if (this.appConf.appMode === APPLICATION_MODE.EMBEDDED && this.appConf.loadSpecificSite) {
      this.intositeEmbeddedService.loadSiteInEmbeddedMode$.next(true);
      return;
    }
    await this.sessionApiSvc.getSessionContext();
    if (ApiTools.defaultSiteId === undefined) {
      return;
    } else {
      PermissionsManager.getUserPermissions();
      if (isNullOrUndefined(this.vpProxyParams) || this.proxyChangedFromStart) {
        if(isNullOrUndefined(this.vpProxyParams)){
          console.log("site loading without await...")
          this.siteLoaderSvc.loadSite(undefined, undefined, false, true);
        }else{
          console.log("site loading with await...")
          await this.siteLoaderSvc.loadSite(undefined, undefined, false, true, true);
        }
      }
    }
    this.serverApi.isSiteAlreadyLoaded.next(true);
  }

  async createVP(params: string[]): Promise<any> {
    let returnVal: any = {};
    switch (params[1]) {
      case '0': {
        returnVal = {
          coordinatesType: 'GIS',
          latitude: Number(params[2]),
          longitude: Number(params[3]),
          altitude: Number(params[4]),
          parentZoneId: undefined,
          accessMode: undefined,
          creatorId: undefined,
          description: undefined,
          parentLayerId: undefined,
          rotation: undefined,
          range: undefined,
          tilt: undefined,
          parentPanoramaId: undefined,
          isDefaultInZone: undefined,
          positionProperty: undefined,
          category: undefined,
          parent: 'MAPS',
          id: undefined,
          name: undefined
        };
        break;
      }
      case '1': {
        returnVal = {
          coordinatesType: 'FACILITY',
          latitude: undefined,
          longitude: undefined,
          altitude: undefined,
          parentZoneId: undefined,
          accessMode: undefined,
          creatorId: undefined,
          description: undefined,
          parentLayerId: undefined,
          rotation: Number(params[5]),
          range: Number(params[6]),
          tilt: Number(params[7]),
          parentPanoramaId: params[8] ?? undefined,
          isDefaultInZone: undefined,
          positionProperty: {x: Number(params[2]), y: Number(params[3]), z: Number(params[4])},
          category: undefined,
          parent: params[8] != null ? 'PANORAMIC' : 'WEBGL',
          id: undefined,
          name: undefined
        };
        break;
      }
    }

    if (returnVal.parent == 'PANORAMIC') {
      let pm: IPlacemark = await this.placemarksApiSvc.getPlacemarkTypeById(returnVal.parentPanoramaId);
      if (pm) {
        if (pm.scanId) {
          try {
            await this.pointCloudSvc.loginToPointCloud();
            await this.pointCloudSvc.checkUserAccessToProject(pm['layer']);
            this.store.dispatch(new GoToProxyViewpoint(returnVal));
          } catch (error) {
            console.error('Error during point cloud login:', error);
          }
        } else {
          this.store.dispatch(new GoToProxyViewpoint(returnVal));
        }
      }
    } else {
      this.store.dispatch(new GoToProxyViewpoint(returnVal));
    }
  }

  async zoomInToObject(params: any): Promise<any> {
    const response = await this.siteApiSvc.getObjectByExternalId(params.siteId, params.externalId, params.app);
    const object = response.object;
    let type = null;
    if (response.objectType === 'PLACEMARK') {
      type = ViewerObjectType.PLACEMARK;
    } else if (response.objectType === 'THREE_D_ELEMENT') {
      type = ViewerObjectType.MODEL;
    }

    // If object is in a layer or zone that is not visible, make them visible before zooming in
    const parentLayerId = object.layer?.id?.value;
    const layer = parentLayerId ? this.store.selectSnapshot(LayersState.getLayers).find(l => l.id == parentLayerId) : null;
    if (layer && layer.visible !== LAYER_VISIBILITY.VISIBLE) {
      layer.visible = LAYER_VISIBILITY.VISIBLE;
      this.store.dispatch(new SetLayer(layer, true));
    }
    
    const parentZoneId = object.parentZone?.id?.value;
    const zone = parentZoneId ? this.store.selectSnapshot(ZonesState.getZones).find(z => z.id == parentZoneId) : null;
    if (zone && !zone.visible) {
      zone.visible = true;
      this.store.dispatch(new SetZone(zone, true));
    }

    const data = {objectType: type, id: object.id?.value};
    this.statusService.registerStatus()
      .pipe(
        filter((status: PriorityStack<MessageAndType>) => status.isEmpty() || status.top().isOnTime),
        take(1)
      ).subscribe((status: PriorityStack<MessageAndType>) => {
        if (params.view === 'facility') {
          this.sceneMode !== SCENE_MODE.Facility && this.store.dispatch(new SetSelectedMode(SCENE_MODE.Facility));
          this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.WEBGL_MANAGER, CMD_ACTIONS.ZOOM_IN_TO_OBJECT, {data});
        } else if (params.view === 'map') {
          this.sceneMode !== SCENE_MODE.Map && this.store.dispatch(new SetSelectedMode(SCENE_MODE.Map));
          this.cmdRouterSvc.sendActionCmd(CMD_TARGETS.MAP_MANAGER, CMD_ACTIONS.GO_TO_VP_MODE, {lat: object.geoLocation?.latitude, lng: object.geoLocation?.longitude, selectedVP: {coordinatesType: CoordinatesType.GIS}});
        }
      });
  }

  setViewpointFromProxy(params: any): void {
    const subscription: Subscription = this.serverApi.isSiteAlreadyLoaded.subscribe(async (isSiteAlreadyLoaded: boolean) => {
      if (params['siteId'] && isSiteAlreadyLoaded) {
        const siteId: number = Number(params['siteId']);
        const targetedSite: ISite = this.store.selectSnapshot<ISite[]>((state: any) => state.StateSites.sites).find((site: ISite) => site.id === siteId);
        const path = params['intosite-site-viewpoint'] ? params['intosite-site-viewpoint'].split(';') : [];
        if (!isNullOrUndefined(targetedSite)) {
          const activeSiteId: number = this.store.selectSnapshot<ISite>((state: any) => state.StateApp.activeSite).id;
          if (targetedSite.id !== activeSiteId) {
            await this.siteLoaderSvc.loadSite(targetedSite, undefined, false, false, true);
          }

          if (params['intosite-object-viewpoint']) {
            this.zoomInToObject(params);
          } else {
            this.createVP(path);
          }
        } else {
          this.store.dispatch(new SetSelectedMode(SCENE_MODE.Load));
          this.store.dispatch(new SetEmptyActiveSite());
          this.store.dispatch(new SetAllSitesNoDefault());
        }
      }
      subscription.unsubscribe();
    });
  }
}
